// services/offline/syncQueue.ts — Offline mutation queue with replay on reconnect

import { idb } from './indexedDB';

export interface SyncOperation {
  id: string;
  createdAt: string;
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  url: string;
  body: unknown;
  retryCount: number;
  maxRetries: number;
}

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000;

/**
 * Enqueue a mutation to be replayed when the device is back online.
 * Used for tree node adds/edits/deletes, profile updates, invite actions, etc.
 */
export async function enqueueSyncOperation(
  operation: Omit<SyncOperation, 'id' | 'createdAt' | 'retryCount' | 'maxRetries'>
): Promise<void> {
  const op: SyncOperation = {
    id: `sync-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    createdAt: new Date().toISOString(),
    retryCount: 0,
    maxRetries: MAX_RETRIES,
    ...operation,
  };

  // Map to the SyncQueueEntry schema used by indexedDB.ts
  await idb.enqueueMutation({
    id: op.id,
    timestamp: op.createdAt,
    method: op.method === 'PUT' ? 'PATCH' : op.method, // idb schema uses PATCH, not PUT
    url: op.url,
    body: op.body,
    retries: op.retryCount,
  });
}

/**
 * Get all pending sync operations ordered by creation time.
 */
export async function getPendingSyncOperations(): Promise<SyncOperation[]> {
  const entries = await idb.getAllSyncQueue();
  return entries
    .map((entry) => ({
      id: entry.id,
      createdAt: entry.timestamp,
      method: entry.method as SyncOperation['method'],
      url: entry.url,
      body: entry.body ?? null,
      retryCount: entry.retries,
      maxRetries: MAX_RETRIES,
    }))
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
}

/**
 * Remove a successfully replayed operation from the queue.
 */
async function removeSyncOperation(id: string): Promise<void> {
  await idb.dequeueMutation(id);
}

/**
 * Replay a single sync operation against the network.
 * Returns true if successful or permanent failure, false if should retry.
 */
async function replayOperation(op: SyncOperation): Promise<boolean> {
  const options: RequestInit = {
    method: op.method,
    headers: { 'Content-Type': 'application/json' },
  };

  if (op.method !== 'DELETE' && op.body !== undefined && op.body !== null) {
    options.body = JSON.stringify(op.body);
  }

  try {
    const response = await fetch(op.url, options);

    if (response.ok) return true;

    // 4xx = permanent failure, remove from queue
    if (response.status >= 400 && response.status < 500) {
      console.warn(`[SyncQueue] Permanent failure for ${op.url}: ${response.status}`);
      return true;
    }

    // 5xx = transient, retry later
    return false;
  } catch {
    // Network error — retry
    return false;
  }
}

/**
 * Process all pending sync operations in FIFO order.
 * Call this when the device comes back online.
 */
export async function flushSyncQueue(): Promise<{
  replayed: number;
  failed: number;
  remaining: number;
}> {
  const operations = await getPendingSyncOperations();

  if (operations.length === 0) return { replayed: 0, failed: 0, remaining: 0 };

  let replayed = 0;
  let failed = 0;

  for (const op of operations) {
    // Give up if max retries exceeded
    if (op.retryCount >= op.maxRetries) {
      await removeSyncOperation(op.id);
      failed++;
      continue;
    }

    const success = await replayOperation(op);

    if (success) {
      await removeSyncOperation(op.id);
      replayed++;
    } else {
      // Update retry count by re-enqueuing with incremented retries
      await idb.enqueueMutation({
        id: op.id,
        timestamp: op.createdAt,
        method: op.method === 'PUT' ? 'PATCH' : op.method,
        url: op.url,
        body: op.body,
        retries: op.retryCount + 1,
      });

      // Exponential backoff
      const delay = RETRY_DELAY_MS * Math.pow(2, op.retryCount);
      await new Promise<void>((r) => setTimeout(r, delay));
    }
  }

  const remaining = (await getPendingSyncOperations()).length;
  return { replayed, failed, remaining };
}

/**
 * Get count of pending sync operations.
 */
export async function getSyncQueueCount(): Promise<number> {
  const ops = await getPendingSyncOperations();
  return ops.length;
}
